import { isLoading } from "apollo/utils";
import { useOperatorAgent } from "core/model/careseekers";
import { formatFromNow } from "core/model/utils/dates";
import {
  AuctionForm,
  DocsFormDataMapped,
  DocsFormName,
  QueryProgress,
  ValueOf,
} from "core/types";
import { Banner, Button, Checkbox } from "ds/ui";
import { useHL7ImportStatusContext } from "dsl/atoms/HL7ImportStatusContext";
import { getFormAssigneeDisplayName } from "dsl/ecosystems/PatientFiles/DocsSection";
import {
  SAVE_AND_CREATE_PDF_ID,
  SAVE_AND_OVERWRITE_PDF_ID,
} from "dsl/ecosystems/PatientForms/RehabForms/RehabFormWrapper";
import {
  CircleCheckBigIcon,
  CircleQuestionMarkIcon,
  FileIcon,
  FolderInputIcon,
  PrinterIcon,
  TrashIcon,
} from "lucide-react";
import { Dispatch, SetStateAction, useRef } from "react";
import { useFormContext } from "react-hook-form";
import { useLocale, useTranslations } from "translations";
import { TranslationComposition } from "translations/helpers";
import { DOCS_FORM_FOOTER_SCREENS, DocsFormFooterScreen } from ".";

export const FOOTER_BANNERS = {
  CREATE_PDF_SUCCESS: "save-success",
  DELETE_ERROR: "delete-error",
  DELETE_SUCCESS: "delete-success",
  OPEN_PDF_ERROR: "open-pdf-error",
  OVERWRITE_PDF_SUCCESS: "overwrite-success",
  SAVE_ERROR: "save-error",
  SAVE_FORM_SUCCESS: "save-form-success",
  SEND_TO_HIS_ERROR: "send-to-his-error",
  SEND_TO_HIS_SUCCESS: "send-to-his-success",
  SEND_TO_OPERATOR_ERROR: "send-to-operator-error",
} as const;

export type FooterBannerType = ValueOf<typeof FOOTER_BANNERS>;

const FooterBanner = ({
  banner,
  onClose,
}: {
  banner: FooterBannerType | null;
  onClose: () => void;
}) => {
  const translations = useTranslations();
  switch (banner) {
    case FOOTER_BANNERS.DELETE_ERROR:
      return (
        <Banner
          color="danger"
          message={translations.extractDocs.splitView.footer.bannerDeleteFailed}
        />
      );
    case FOOTER_BANNERS.DELETE_SUCCESS:
      return (
        <Banner
          color="success"
          message={
            translations.extractDocs.splitView.footer
              .titleBannerDeletedSuccessfully
          }
          withClose
          onClose={onClose}
        />
      );
    case FOOTER_BANNERS.OVERWRITE_PDF_SUCCESS:
      return (
        <Banner
          color="success"
          message={
            translations.extractDocs.splitView.footer.overwritePdfSuccessBanner
          }
          withClose
          onClose={onClose}
        />
      );
    case FOOTER_BANNERS.CREATE_PDF_SUCCESS:
      return (
        <Banner
          color="success"
          message={
            translations.extractDocs.splitView.footer.createPdfSuccessBanner
          }
          withClose
          onClose={onClose}
        />
      );
    case FOOTER_BANNERS.SAVE_ERROR:
      return (
        <Banner
          color="danger"
          message={translations.extractDocs.splitView.footer.bannerSaveFailed}
          withClose
          onClose={onClose}
        />
      );
    case FOOTER_BANNERS.SEND_TO_HIS_SUCCESS:
      return (
        <Banner
          color="success"
          message={translations.extractDocs.splitView.footer.sendToKisSuccess}
          withClose
          onClose={onClose}
        />
      );
    case FOOTER_BANNERS.OPEN_PDF_ERROR:
      return (
        <Banner
          color="danger"
          message={
            translations.extractDocs.splitView.footer.bannerOpenPdfFailed
          }
          withClose
          onClose={onClose}
        />
      );
    case FOOTER_BANNERS.SEND_TO_HIS_ERROR:
      return (
        <Banner
          color="danger"
          message={
            translations.extractDocs.splitView.footer.bannerSendToHisFailed
          }
          withClose
          onClose={onClose}
        />
      );
    case FOOTER_BANNERS.SEND_TO_OPERATOR_ERROR:
      return (
        <Banner
          color="danger"
          message={
            translations.extractDocs.splitView.footer.bannerSendToOperatorFailed
          }
          withClose
          onClose={onClose}
        />
      );
    case FOOTER_BANNERS.SAVE_FORM_SUCCESS:
      return (
        <Banner
          color="success"
          message={
            translations.extractDocs.splitView.footer.saveFormConfirmationBanner
          }
          withClose
          onClose={onClose}
        />
      );
    default:
      return null;
  }
};

export function FormFooter<T extends DocsFormName>({
  canPrint,
  canSendToHis,
  fileName,
  footerBanner,
  footerScreen,
  form,
  handleDeleteForm,
  handleOpenPdf,
  handleSendToHis,
  hasExistingFormPdf,
  hasFeedbackChanges,
  hasPreviousFormValue,
  isConfirmationChecked,
  onBack,
  onSubmitProgress,
  progressDeleteForm,
  progressOpenPdf,
  progressSendToHis,
  progressUploadFormPdf,
  setConfirmationChecked,
  setFooterBanner,
  setFooterScreen,
}: {
  fileName: string;
  hasExistingFormPdf: boolean;
  canPrint: boolean;
  canSendToHis: boolean;
  footerBanner: FooterBannerType | null;
  footerScreen: DocsFormFooterScreen;
  hasFeedbackChanges: boolean;
  hasPreviousFormValue: boolean;
  isConfirmationChecked: boolean;
  onBack: () => void;
  onSubmitProgress: QueryProgress;
  setConfirmationChecked: Dispatch<SetStateAction<boolean>>;
  handleDeleteForm: () => void;
  setFooterBanner: (banner: FooterBannerType | null) => void;
  setFooterScreen: Dispatch<SetStateAction<DocsFormFooterScreen>>;
  handleOpenPdf: () => void;
  handleSendToHis: () => void;
  progressDeleteForm: QueryProgress;
  progressOpenPdf: QueryProgress;
  progressSendToHis: QueryProgress;
  progressUploadFormPdf: QueryProgress;
  form: AuctionForm | undefined;
}) {
  const {
    formState: { disabled: isRootFormDisabled, isDirty },
  } = useFormContext<DocsFormDataMapped<T>>();
  const hasChanges = isDirty || hasFeedbackChanges;
  const translations = useTranslations();

  const locale = useLocale();
  const { hasTwoWaySync } = useHL7ImportStatusContext();
  const { active: isOperatorAgentActive } = useOperatorAgent();
  const canSendToHisConnection = hasTwoWaySync || isOperatorAgentActive;
  const formAssigneeDisplayName = getFormAssigneeDisplayName(form);
  const updatedAt = form?.updated_at;

  const hasExistingFormPdfRef = useRef(hasExistingFormPdf);
  if (!isLoading(onSubmitProgress)) {
    hasExistingFormPdfRef.current = hasExistingFormPdf;
  }

  const FormDetails = () => {
    if (!updatedAt || !formAssigneeDisplayName) return null;

    return (
      <p className="flex gap-1 text-body-md">
        {translations.extractDocs.splitView.footer.lastSavedTimestamp}
        <span className="font-bold">{formAssigneeDisplayName}</span>
        {translations.extractDocs.splitView.footer.lastSavedDate}
        <span className="font-bold">
          {formatFromNow(updatedAt ?? 0, locale)}
        </span>
      </p>
    );
  };

  switch (footerScreen) {
    case DOCS_FORM_FOOTER_SCREENS.WARN_OPEN_PDF: {
      return (
        <div
          key={DOCS_FORM_FOOTER_SCREENS.WARN_OPEN_PDF}
          className="sticky bottom-0 z-confirm-modal flex w-full flex-col gap-8 border-t border-gray-400 bg-white p-4 shadow-2xl [clip-path:inset(-50px_0px_-50px_-50px)]"
        >
          <div className="flex gap-4">
            <PrinterIcon className="text-primary" />
            <p className="text-heading-md font-bold">
              {translations.extractDocs.splitView.footer.buttonPrint}
            </p>
          </div>
          <Banner
            color="warning"
            heading={
              translations.extractDocs.splitView.footer.titleWarningNotSaved
            }
            message={
              <div className="flex flex-col gap-4">
                <p>
                  <TranslationComposition
                    translations={
                      translations.extractDocs.splitView.footer
                        .descriptionWarningNotSaved
                    }
                    withOptions
                  >
                    {([before, boldText, after]) => (
                      <>
                        {before}
                        <b>{boldText}</b>
                        {after}
                      </>
                    )}
                  </TranslationComposition>
                </p>
                <p>
                  <TranslationComposition
                    translations={
                      translations.extractDocs.splitView.footer
                        .descriptionWarningPrint
                    }
                    withOptions
                  >
                    {([before, boldText, after]) => (
                      <>
                        {before}
                        <b>{boldText}</b>
                        {after}
                      </>
                    )}
                  </TranslationComposition>
                </p>
              </div>
            }
          />
          <FormDetails />
          <div className="flex w-full items-center justify-end gap-4">
            <Button
              color="primary"
              id="close-warning-open-pdf-screen"
              size="sm"
              variant="faded"
              onPress={onBack}
            >
              {translations.extractDocs.splitView.footer.buttonCancel}
            </Button>
            <Button
              color="primary"
              endContent={<PrinterIcon />}
              id="open-pdf"
              isLoading={isLoading(progressOpenPdf)}
              size="sm"
              variant="solid"
              onPress={handleOpenPdf}
            >
              {translations.extractDocs.splitView.footer.buttonPrint}
            </Button>
          </div>
        </div>
      );
    }
    case DOCS_FORM_FOOTER_SCREENS.WARN_SEND_TO_HIS: {
      return (
        <div
          key={DOCS_FORM_FOOTER_SCREENS.WARN_SEND_TO_HIS}
          className="sticky bottom-0 z-confirm-modal flex w-full flex-col gap-8 border-t border-gray-400 bg-white p-4 shadow-2xl [clip-path:inset(-50px_0px_-50px_-50px)]"
        >
          <div className="flex gap-4">
            <FolderInputIcon className="text-primary" />
            <p className="text-heading-md font-bold">
              {translations.extractDocs.splitView.footer.buttonSendToKis}
            </p>
          </div>
          <Banner
            color="warning"
            heading={
              translations.extractDocs.splitView.footer.titleWarningNotSaved
            }
            message={
              <div className="flex flex-col gap-4">
                <p>
                  <TranslationComposition
                    translations={
                      translations.extractDocs.splitView.footer
                        .descriptionWarningNotSaved
                    }
                    withOptions
                  >
                    {([before, boldText, after]) => (
                      <>
                        {before}
                        <b>{boldText}</b>
                        {after}
                      </>
                    )}
                  </TranslationComposition>
                </p>
                <p>
                  <TranslationComposition
                    translations={
                      translations.extractDocs.splitView.footer
                        .descriptionWarningKis
                    }
                    withOptions
                  >
                    {([before, boldText, after]) => (
                      <>
                        {before}
                        <b>{boldText}</b>
                        {after}
                      </>
                    )}
                  </TranslationComposition>
                </p>
              </div>
            }
          />
          <FormDetails />
          <div className="flex w-full items-center justify-end gap-4">
            <Button
              color="primary"
              id="close-warning-open-pdf-screen"
              size="sm"
              variant="faded"
              onPress={onBack}
            >
              {translations.extractDocs.splitView.footer.buttonCancel}
            </Button>
            <Button
              color="primary"
              endContent={<FolderInputIcon />}
              id="send-to-his"
              isLoading={isLoading(progressSendToHis)}
              size="sm"
              variant="solid"
              onPress={handleSendToHis}
            >
              {translations.extractDocs.splitView.footer.buttonSendToKis}
            </Button>
          </div>
        </div>
      );
    }
    case DOCS_FORM_FOOTER_SCREENS.UPLOAD_PDF_CONFIRMATION: {
      return (
        <div
          key={DOCS_FORM_FOOTER_SCREENS.UPLOAD_PDF_CONFIRMATION}
          className="sticky bottom-0 z-confirm-modal flex w-full flex-col gap-8 border-t border-gray-400 bg-white p-4 shadow-2xl [clip-path:inset(-50px_0px_-50px_-50px)]"
        >
          <div className="flex gap-4">
            <CircleQuestionMarkIcon className="text-primary" />
            <div className="flex w-full flex-col gap-4 pt-0.5">
              <p className="text-heading-md font-bold">
                {hasExistingFormPdfRef.current
                  ? translations.extractDocs.splitView.footer.overwritePdfTitle
                  : translations.extractDocs.splitView.footer.createPdfTitle}
              </p>
              {hasExistingFormPdfRef.current && (
                <p className="text-body-lg">
                  {translations.extractDocs.splitView.footer.overwritePdfExistingDescription(
                    { pdfName: fileName },
                  )}
                </p>
              )}
              <p className="text-body-lg">
                {
                  translations.extractDocs.splitView.footer
                    .createOverWritePdfDescription
                }
              </p>
            </div>
          </div>
          <Checkbox
            className="gap-2 font-bold"
            isRequired
            label={
              translations.extractDocs.splitView.footer.checkboxConfirmation
            }
            name="confirmation"
            size="md"
            onValueChange={setConfirmationChecked}
          />
          <div className="flex w-full items-center justify-end gap-4">
            <Button
              color="primary"
              id="close-confirmation-screen"
              isDisabled={isLoading(onSubmitProgress)}
              size="sm"
              variant="faded"
              onPress={onBack}
            >
              {translations.extractDocs.splitView.footer.buttonCancel}
            </Button>
            <Button
              color="primary"
              endContent={<CircleCheckBigIcon />}
              id={
                hasExistingFormPdfRef.current
                  ? SAVE_AND_OVERWRITE_PDF_ID
                  : SAVE_AND_CREATE_PDF_ID
              }
              isDisabled={!isConfirmationChecked}
              isLoading={isLoading(onSubmitProgress)}
              size="sm"
              type="submit"
            >
              {hasExistingFormPdfRef.current
                ? translations.extractDocs.splitView.footer.overwritePdfButton
                : translations.extractDocs.splitView.footer.createPdfButton}
            </Button>
          </div>
        </div>
      );
    }
    case DOCS_FORM_FOOTER_SCREENS.SAVE_FORM_CONFIRMATION: {
      return (
        <div
          key={DOCS_FORM_FOOTER_SCREENS.SAVE_FORM_CONFIRMATION}
          className="sticky bottom-0 z-confirm-modal flex w-full flex-col gap-8 border-t border-gray-400 bg-white p-4 shadow-2xl [clip-path:inset(-50px_0px_-50px_-50px)]"
        >
          <div className="flex gap-4">
            <CircleQuestionMarkIcon className="text-primary" />
            <div className="flex w-full flex-col gap-4 pt-0.5">
              <p className="text-heading-md font-bold">
                {translations.extractDocs.splitView.footer.saveFormTitle}
              </p>
              <p className="text-body-lg">
                {
                  translations.extractDocs.splitView.footer
                    .saveFormConfirmationDescription
                }
              </p>
            </div>
          </div>
          <Checkbox
            className="gap-2 font-bold"
            isRequired
            label={
              translations.extractDocs.splitView.footer.checkboxConfirmation
            }
            name="confirmation"
            size="md"
            onValueChange={setConfirmationChecked}
          />
          <div className="flex w-full items-center justify-end gap-4">
            <Button
              color="primary"
              id="close-save-form-confirmation-screen"
              size="sm"
              variant="faded"
              onPress={onBack}
            >
              {translations.extractDocs.splitView.footer.buttonCancel}
            </Button>
            <Button
              color="primary"
              endContent={<CircleCheckBigIcon />}
              id="confirm-save-form"
              isDisabled={!isConfirmationChecked}
              isLoading={isLoading(onSubmitProgress)}
              size="sm"
              type="submit"
            >
              {translations.extractDocs.splitView.footer.saveFormButton}
            </Button>
          </div>
        </div>
      );
    }
    case DOCS_FORM_FOOTER_SCREENS.DELETE_FORM_CONFIRMATION: {
      return (
        <div
          key={DOCS_FORM_FOOTER_SCREENS.DELETE_FORM_CONFIRMATION}
          className="sticky bottom-0 z-confirm-modal flex w-full flex-col gap-8 border-t border-gray-400 bg-white p-4 shadow-2xl [clip-path:inset(-50px_0px_-50px_-50px)]"
        >
          <div className="flex gap-4">
            <CircleQuestionMarkIcon className="text-primary" />
            <div className="flex w-full flex-col gap-2 pt-0.5">
              <p className="text-heading-md font-bold">
                {translations.extractDocs.splitView.footer.deleteFormTitle}
              </p>
              <p className="text-body-lg">
                {
                  translations.extractDocs.splitView.footer
                    .descriptionDeleteConfirmation
                }
              </p>
            </div>
          </div>
          <div className="flex w-full items-center justify-end gap-4">
            <Button
              color="black"
              id="close-delete-confirmation-screen"
              size="sm"
              variant="light"
              onPress={onBack}
            >
              {translations.extractDocs.splitView.footer.buttonCancel}
            </Button>
            <Button
              color="danger"
              endContent={<TrashIcon />}
              id="delete-form"
              isLoading={isLoading(progressDeleteForm)}
              size="sm"
              onPress={handleDeleteForm}
            >
              {
                translations.extractDocs.splitView.footer
                  .deleteFormConfirmationButton
              }
            </Button>
          </div>
        </div>
      );
    }
    default:
      return (
        <div
          key={DOCS_FORM_FOOTER_SCREENS.DEFAULT}
          className="sticky bottom-0 z-confirm-modal flex w-full flex-col gap-4 border-t border-gray-400 bg-white p-4 shadow-2xl [clip-path:inset(-50px_0px_-50px_-50px)]"
        >
          <FooterBanner
            banner={footerBanner}
            onClose={() => setFooterBanner(null)}
          />
          <FormDetails />
          <div className="flex flex-wrap items-center justify-between gap-4">
            <Button
              color="danger"
              id="delete-form"
              isDisabled={
                isRootFormDisabled || (!hasPreviousFormValue && !hasChanges)
              }
              size="sm"
              startContent={<TrashIcon />}
              variant="light"
              onPress={() => {
                setFooterScreen(
                  DOCS_FORM_FOOTER_SCREENS.DELETE_FORM_CONFIRMATION,
                );
              }}
            >
              {translations.patientForms.menu.buttonDelete}
            </Button>
            <div className="flex flex-wrap gap-4">
              {hasPreviousFormValue && canPrint ? (
                <Button
                  color="primary"
                  id="open-pdf"
                  isDisabled={
                    isRootFormDisabled || !hasExistingFormPdf || isDirty
                  }
                  isLoading={isLoading(progressOpenPdf)}
                  size="sm"
                  startContent={<PrinterIcon />}
                  variant="ghost"
                  onPress={handleOpenPdf}
                >
                  {translations.extractDocs.splitView.footer.buttonPrint}
                </Button>
              ) : null}
              {hasPreviousFormValue &&
              canSendToHisConnection &&
              canSendToHis ? (
                <Button
                  color="primary"
                  id="send-to-his"
                  isDisabled={isRootFormDisabled || isDirty}
                  isLoading={isLoading(progressSendToHis)}
                  size="sm"
                  startContent={<FolderInputIcon />}
                  variant="ghost"
                  onPress={handleSendToHis}
                >
                  {translations.extractDocs.splitView.footer.buttonSendToKis}
                </Button>
              ) : null}
              {hasPreviousFormValue ? (
                <Button
                  color="primary"
                  id="create-pdf"
                  isDisabled={isRootFormDisabled || isDirty}
                  isLoading={isLoading(progressUploadFormPdf)}
                  size="sm"
                  startContent={<FileIcon />}
                  variant="ghost"
                  onPress={() => {
                    setFooterScreen(
                      DOCS_FORM_FOOTER_SCREENS.UPLOAD_PDF_CONFIRMATION,
                    );
                  }}
                >
                  {translations.extractDocs.splitView.footer.createPdfButton}
                </Button>
              ) : null}
              <Button
                color="primary"
                endContent={<CircleCheckBigIcon />}
                id="save-form"
                isDisabled={isRootFormDisabled || !hasChanges}
                size="sm"
                onPress={() => {
                  setFooterScreen(
                    DOCS_FORM_FOOTER_SCREENS.SAVE_FORM_CONFIRMATION,
                  );
                }}
              >
                {translations.extractDocs.splitView.footer.buttonReviewAndSave}
              </Button>
            </div>
          </div>
        </div>
      );
  }
}
